import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface Props {
  aperto: boolean;
  onChiudi: () => void;
  /** Titolo visibile e nome del dialogo per chi legge con lo screen reader. */
  titolo: string;
  /** Riga piccola sopra il titolo. */
  sopra?: string;
  /** Una parola al posto della «X» (es. «Annulla»), quando chiudere non è solo chiudere. */
  testoChiudi?: string;
  /** Una parte ferma in fondo (es. i tasti): con un contenuto lungo scorre solo quello che sta in mezzo. */
  piede?: ReactNode;
  /** Dai 1024 px il pannello sta al centro dello schermo invece che in basso. */
  centrato?: boolean;
  children: ReactNode;
}

const FOCUSABILI = "button:not([disabled]), select:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex='-1'])";

/** Pannello che sale dal basso (telefono). Si chiude con la «X», con Esc e toccando il velo; il focus entra
 *  all'apertura, resta dentro con Tab e torna a chi l'ha aperto alla chiusura. */
export function PannelloBasso({ aperto, onChiudi, titolo, sopra, testoChiudi, piede, centrato = false, children }: Props) {
  const pannello = useRef<HTMLDivElement>(null);
  // onChiudi in un ref: una callback nuova a ogni render non deve riaprire l'effetto (e rubare il focus).
  const chiudi = useRef(onChiudi);
  useEffect(() => { chiudi.current = onChiudi; });

  useEffect(() => {
    if (!aperto) return;
    const ritorno = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    pannello.current?.focus();

    function tasto(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        chiudi.current();
        return;
      }
      if (e.key !== "Tab" || !pannello.current) return;
      const elementi = Array.from(pannello.current.querySelectorAll<HTMLElement>(FOCUSABILI));
      if (elementi.length === 0) return;
      const primo = elementi[0];
      const ultimo = elementi[elementi.length - 1];
      const qui = document.activeElement;
      const fuori = !pannello.current.contains(qui) || qui === pannello.current;
      if (e.shiftKey && (fuori || qui === primo)) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && (fuori || qui === ultimo)) {
        e.preventDefault();
        primo.focus();
      }
    }
    document.addEventListener("keydown", tasto);
    return () => {
      document.removeEventListener("keydown", tasto);
      document.body.style.overflow = overflow;
      if (ritorno?.isConnected) ritorno.focus();
    };
  }, [aperto]);

  if (!aperto) return null;
  const testa = (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 pt-1">
        {sopra && <p className="text-xs text-muted-foreground">{sopra}</p>}
        <h2 className="font-heading text-lg font-semibold leading-tight text-text break-words">{titolo}</h2>
      </div>
      {testoChiudi ? (
        <button
          type="button"
          onClick={onChiudi}
          className="-mr-2 inline-flex h-11 shrink-0 items-center rounded-lg px-2 text-sm font-medium text-muted-foreground cursor-pointer hover:bg-surface hover:text-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          {testoChiudi}
        </button>
      ) : (
        <button
          type="button"
          aria-label="Chiudi"
          onClick={onChiudi}
          className="-mr-2 inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground cursor-pointer hover:bg-surface transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          <X size={20} aria-hidden="true" />
        </button>
      )}
    </div>
  );
  const base = `relative max-h-[90dvh] w-full max-w-lg rounded-t-2xl bg-card shadow-2xl outline-none animate-in slide-in-from-bottom duration-200 ease-out motion-reduce:animate-none ${centrato ? "lg:rounded-2xl" : ""}`;
  return createPortal(
    <div className={`fixed inset-0 z-50 flex items-end justify-center ${centrato ? "lg:items-center lg:p-6" : ""}`}>
      <div
        data-testid="velo"
        aria-hidden="true"
        onClick={onChiudi}
        className="absolute inset-0 bg-black/45 animate-in fade-in duration-200 motion-reduce:animate-none"
      />
      {piede ? (
        // titolo fermo in cima, tasti fermi in fondo: scorre solo il mezzo
        <div ref={pannello} role="dialog" aria-modal="true" aria-label={titolo} tabIndex={-1} className={`${base} flex flex-col`}>
          <div className="px-4 pt-4">{testa}</div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4">{children}</div>
          <div className="border-t border-border px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">{piede}</div>
        </div>
      ) : (
        <div
          ref={pannello}
          role="dialog"
          aria-modal="true"
          aria-label={titolo}
          tabIndex={-1}
          className={`${base} overflow-y-auto overscroll-contain px-4 pt-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]`}
        >
          {testa}
          <div className="mt-4">{children}</div>
        </div>
      )}
    </div>,
    document.body,
  );
}
